import type { ISubscription } from "@features/shared/entities/subscription";

type UpiUpsellEligibility = {
    paymentSystem: string;
    currency?: string | null;
    userCountry?: string | null;
    subscriptions: ISubscription[];
};

type UpiUpsellMode = UpiUpsellEligibility & {
    hasRecurringToken: boolean | null;
};

const normalize = (value?: string | null) => value?.trim().toUpperCase() ?? "";

export const resolveUpsellCountry = ({
    userCountry,
    subscriptions
}: Pick<UpiUpsellEligibility, "userCountry" | "subscriptions">): string => {
    const currentCountry = normalize(userCountry);
    if (currentCountry) return currentCountry;

    const solidgateInrSubscription = subscriptions.find(
        (subscription) =>
            normalize(subscription.platform?.code) === "SOLIDGATE" && normalize(subscription.currency) === "INR"
    );

    return normalize(solidgateInrSubscription?.country);
};

export const isIndiaInrUpsell = ({
    paymentSystem,
    currency,
    userCountry,
    subscriptions
}: UpiUpsellEligibility): boolean => {
    const country = resolveUpsellCountry({ userCountry, subscriptions });

    return (
        normalize(paymentSystem) === "SOLIDGATE" &&
        normalize(currency) === "INR" &&
        (country === "IN" || country === "IND")
    );
};

// Solidgate APMs a one-time upsell is paid with when the student has no
// one-click token: UPI Intent (India, INR) and Pix QR (BRL — Pix Automático
// subscribers, whose mandate takes only the subscription's scheduled charges).
export type UpsellApmMethod = "upi" | "pix-qr";

export const isBrlUpsell = ({ paymentSystem, currency }: Pick<UpiUpsellEligibility, "paymentSystem" | "currency">): boolean =>
    normalize(paymentSystem) === "SOLIDGATE" && normalize(currency) === "BRL";

// null while the one-click capability is unknown or when the token can be charged.
export const resolveUpsellApmMethod = ({ hasRecurringToken, ...eligibility }: UpiUpsellMode): UpsellApmMethod | null => {
    if (hasRecurringToken !== false) return null;
    if (isIndiaInrUpsell(eligibility)) return "upi";
    if (isBrlUpsell(eligibility)) return "pix-qr";
    return null;
};

export const shouldUseUpiForUpsell = (mode: UpiUpsellMode): boolean => resolveUpsellApmMethod(mode) === "upi";

// The legal line under the CTA names the method the customer pays with. Keys are
// the English copy (locales/app/*/app.json); the bold variants are for the
// pages that render it as HTML.
const UPSELL_APM_LEGAL: Record<UpsellApmMethod, { plain: string; bold: string }> = {
    upi: {
        plain: 'By selecting "Pay with UPI", you agree to a one-time charge of {{price}} for the selected add-ons. You will authorize this payment in your UPI app.',
        bold: 'By selecting <b>"Pay with UPI"</b>, you agree to a one-time charge of {{price}} for the selected add-ons. You will authorize this payment in your UPI app.'
    },
    "pix-qr": {
        plain: "By paying with Pix, you agree to a one-time charge of {{price}} for the selected add-ons. You will authorize this payment in your bank app.",
        bold: "By paying with <b>Pix</b>, you agree to a one-time charge of {{price}} for the selected add-ons. You will authorize this payment in your bank app."
    }
};

export const upsellApmLegalKey = (method: UpsellApmMethod, { bold = false }: { bold?: boolean } = {}): string =>
    UPSELL_APM_LEGAL[method][bold ? "bold" : "plain"];
